import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { User, ShoppingBag, Clock, MapPin, Phone, Mail, Edit, Save, LogOut, Eye, Package, FileText, Star, Heart, Gift } from 'lucide-react';
import { Logo } from '../components/Logo';
import { useCart } from '../context/CartContext';

const statusColors = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
  preparing: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
  ready: 'bg-green-500/20 text-green-400 border-green-500/50',
  delivered: 'bg-gray-500/20 text-gray-400 border-gray-500/50',
  cancelled: 'bg-red-500/20 text-red-400 border-red-500/50',
};

export default function CustomerAccountPage() {
  const navigate = useNavigate();
  const { customer, logout, getOrderHistory, getProfile, updateProfile, isAuthenticated, loading: authLoading, token: customerToken } = useCustomerAuth();
  const { itemCount } = useCart();
  
  const [orders, setOrders] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loyalty, setLoyalty] = useState(null);
  const [favouriteItems, setFavouriteItems] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: ''
  });

  useEffect(() => {
    document.title = 'My Account - SizzleBox';
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/account' } } });
      return;
    }

    const fetchData = async () => {
      if (!isAuthenticated) return;
      
      try {
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        const headers = customerToken ? { Authorization: `Bearer ${customerToken}` } : {};
        const [ordersData, profileData] = await Promise.all([
          getOrderHistory(),
          getProfile()
        ]);
        setOrders(ordersData || []);
        setProfile(profileData);
        
        // Fetch loyalty points and favourites
        try {
          const [loyaltyRes, favsRes] = await Promise.all([
            fetch(`${API}/customer/loyalty`, { headers }).then(r => r.ok ? r.json() : null),
            fetch(`${API}/customer/favourites`, { headers }).then(r => r.ok ? r.json() : null),
          ]);
          if (loyaltyRes) setLoyalty(loyaltyRes);
          if (favsRes) setFavouriteItems(favsRes.items || []);
        } catch (_) {}
        
        if (profileData) {
          setFormData({
            name: profileData.user?.name || '',
            phone: profileData.customer?.phone || '',
            address: profileData.customer?.address || ''
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, authLoading, navigate, getOrderHistory, getProfile]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const success = await updateProfile(formData);
      if (success) {
        toast.success('Profile updated successfully');
        setEditing(false);
        // Refresh profile
        const profileData = await getProfile();
        setProfile(profileData);
      } else {
        toast.error('Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-fire-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="customer-account-page">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center">
              <Logo className="h-12" />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/cart" className="relative">
                <ShoppingBag className="w-6 h-6 text-white" />
                {itemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-fire-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </Link>
              <Link to="/menu">
                <Button className="bg-fire-500 hover:bg-fire-600 text-white rounded-full px-6">
                  Order Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-28 pb-20">
        <div className="container mx-auto px-6 max-w-4xl">
          {/* Welcome Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="font-heading text-4xl text-white mb-2">
                Hello, {customer?.name || 'Customer'} 👋
              </h1>
              <p className="text-gray-400">Manage your account and view order history</p>
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="border-white/10 text-white hover:bg-white/10"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-card rounded-xl border border-white/10 p-4 text-center">
              <ShoppingBag className="w-8 h-8 text-fire-500 mx-auto mb-2" />
              <p className="text-2xl text-white font-bold">{profile?.customer?.total_orders || orders.length}</p>
              <p className="text-gray-500 text-sm">Total Orders</p>
            </div>
            <div className="bg-card rounded-xl border border-white/10 p-4 text-center">
              <Package className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl text-white font-bold">
                {orders.filter(o => o.status === 'pending' || o.status === 'preparing').length}
              </p>
              <p className="text-gray-500 text-sm">Active Orders</p>
            </div>
            <div className="bg-card rounded-xl border border-white/10 p-4 text-center">
              <Clock className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <p className="text-2xl text-white font-bold">
                {orders.filter(o => o.status === 'delivered').length}
              </p>
              <p className="text-gray-500 text-sm">Completed</p>
            </div>
            <div className="bg-card rounded-xl border border-white/10 p-4 text-center">
              <span className="text-3xl mb-2 block">₦</span>
              <p className="text-2xl text-white font-bold">
                {(profile?.customer?.total_spent || 0).toLocaleString()}
              </p>
              <p className="text-gray-500 text-sm">Total Spent</p>
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="orders" className="space-y-6">
            <TabsList className="bg-card border border-white/10 flex-wrap h-auto gap-1">
              <TabsTrigger value="orders" className="data-[state=active]:bg-fire-500">
                <ShoppingBag className="w-4 h-4 mr-2" /> Orders
              </TabsTrigger>
              <TabsTrigger value="loyalty" className="data-[state=active]:bg-fire-500">
                <Gift className="w-4 h-4 mr-2" /> Points
              </TabsTrigger>
              <TabsTrigger value="favourites" className="data-[state=active]:bg-fire-500">
                <Heart className="w-4 h-4 mr-2" /> Favourites
              </TabsTrigger>
              <TabsTrigger value="profile" className="data-[state=active]:bg-fire-500">
                <User className="w-4 h-4 mr-2" /> Profile
              </TabsTrigger>
            </TabsList>

            {/* Orders Tab */}
            <TabsContent value="orders" className="space-y-4">
              {orders.length === 0 ? (
                <div className="bg-card rounded-xl border border-white/10 p-12 text-center">
                  <ShoppingBag className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl text-white mb-2">No orders yet</h3>
                  <p className="text-gray-400 mb-6">Start ordering delicious food!</p>
                  <Link to="/menu">
                    <Button className="bg-fire-500 hover:bg-fire-600 text-white rounded-full px-8">
                      Browse Menu
                    </Button>
                  </Link>
                </div>
              ) : (
                orders.map(order => (
                  <div
                    key={order.id}
                    className="bg-card rounded-xl border border-white/10 p-6"
                    data-testid={`order-${order.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-fire-500 font-mono font-bold text-lg">#{order.order_number}</h3>
                          <Badge className={`${statusColors[order.status]} border capitalize`}>
                            {order.status}
                          </Badge>
                        </div>
                        <p className="text-gray-400 text-sm">{formatDate(order.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-white font-bold text-xl">₦{order.total.toLocaleString()}</p>
                          <p className="text-gray-500 text-sm">{order.items.length} items</p>
                        </div>
                        <div className="flex gap-2">
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/receipt/${order.id}`} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="outline" className="border-white/10 text-white hover:bg-white/10" data-testid={`receipt-btn-${order.id}`}>
                              <FileText className="w-4 h-4 mr-1" /> Receipt
                            </Button>
                          </a>
                          <Link to={`/order/${order.id}`}>
                            <Button size="sm" variant="outline" className="border-white/10 text-white hover:bg-white/10">
                              <Eye className="w-4 h-4 mr-1" /> View
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                    
                    {/* Order Items Preview */}
                    <div className="flex flex-wrap gap-2">
                      {order.items.slice(0, 3).map((item, idx) => (
                        <span key={idx} className="bg-black/50 text-gray-300 text-sm px-3 py-1 rounded-full">
                          {item.quantity}x {item.name}
                        </span>
                      ))}
                      {order.items.length > 3 && (
                        <span className="bg-black/50 text-gray-500 text-sm px-3 py-1 rounded-full">
                          +{order.items.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </TabsContent>


            {/* Loyalty Points Tab */}
            <TabsContent value="loyalty">
              <div className="bg-card rounded-xl border border-white/10 p-6" data-testid="loyalty-tab">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-fire-500/20 rounded-full mb-4">
                    <Gift className="w-8 h-8 text-fire-500" />
                  </div>
                  <h3 className="text-white font-bold text-2xl mb-1">Loyalty Points</h3>
                  <p className="text-gray-400 text-sm">Earn points with every order, redeem for discounts!</p>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="bg-black/30 rounded-xl p-4 text-center">
                    <p className="text-fire-500 font-bold text-3xl">{loyalty?.points || 0}</p>
                    <p className="text-gray-400 text-sm">Points Balance</p>
                  </div>
                  <div className="bg-black/30 rounded-xl p-4 text-center">
                    <p className="text-green-400 font-bold text-3xl">₦{(loyalty?.points_value || 0).toLocaleString()}</p>
                    <p className="text-gray-400 text-sm">Points Value</p>
                  </div>
                  <div className="bg-black/30 rounded-xl p-4 text-center">
                    <p className="text-white font-bold text-3xl">{loyalty?.total_orders || 0}</p>
                    <p className="text-gray-400 text-sm">Total Orders</p>
                  </div>
                  <div className="bg-black/30 rounded-xl p-4 text-center">
                    <p className="text-yellow-400 font-bold text-3xl">{loyalty?.next_reward || 0}</p>
                    <p className="text-gray-400 text-sm">Points to Next Reward</p>
                  </div>
                </div>

                <div className="bg-fire-500/10 border border-fire-500/30 rounded-xl p-4 text-center">
                  <p className="text-white font-semibold mb-1">How it works</p>
                  <p className="text-gray-400 text-sm">Earn <span className="text-fire-500 font-bold">10 points</span> for every ₦1,000 spent. Redeem points at checkout — each point is worth ₦10!</p>
                </div>
              </div>
            </TabsContent>

            {/* Favourites Tab */}
            <TabsContent value="favourites">
              <div data-testid="favourites-tab">
                {favouriteItems.length === 0 ? (
                  <div className="bg-card rounded-xl border border-white/10 p-12 text-center">
                    <Heart className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-xl text-white mb-2">No favourites yet</h3>
                    <p className="text-gray-400 mb-6">Tap the heart on any menu item to save it here</p>
                    <Link to="/menu">
                      <Button className="bg-fire-500 hover:bg-fire-600 text-white rounded-full px-8">
                        Browse Menu
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {favouriteItems.map(item => (
                      <div key={item.id} className="bg-card rounded-xl border border-white/10 p-4 flex gap-4" data-testid={`fav-item-${item.id}`}>
                        <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                          <img src={item.image_url || 'https://via.placeholder.com/80'} alt={item.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-white font-bold truncate">{item.name}</h4>
                          <p className="text-fire-500 font-bold">₦{item.price?.toLocaleString()}</p>
                          {item.avg_rating > 0 && (
                            <div className="flex items-center gap-1 mt-1">
                              <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                              <span className="text-yellow-400 text-xs">{item.avg_rating}</span>
                            </div>
                          )}
                        </div>
                        <Link to="/menu" className="flex-shrink-0 self-center">
                          <Button size="sm" className="bg-fire-500 hover:bg-fire-600 text-white rounded-full text-xs">Order</Button>
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>


            {/* Profile Tab */}
            <TabsContent value="profile">
              <div className="bg-card rounded-xl border border-white/10 p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-white font-bold text-xl">Profile Information</h3>
                  {!editing ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditing(true)}
                      className="border-white/10 text-white hover:bg-white/10"
                      data-testid="edit-profile-btn"
                    >
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={handleSaveProfile}
                      disabled={saving}
                      className="bg-fire-500 hover:bg-fire-600"
                      data-testid="save-profile-btn"
                    >
                      <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
                    </Button>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <Label className="text-gray-400 mb-2 block flex items-center gap-2">
                      <User className="w-4 h-4" /> Full Name
                    </Label>
                    {editing ? (
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="bg-black/50 border-white/10 text-white"
                        data-testid="profile-name"
                      />
                    ) : (
                      <p className="text-white text-lg">{formData.name || '-'}</p>
                    )}
                  </div>

                  <div>
                    <Label className="text-gray-400 mb-2 block flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Email
                    </Label>
                    <p className="text-white text-lg">{customer?.email || '-'}</p>
                    {editing && <p className="text-gray-500 text-sm mt-1">Email cannot be changed</p>}
                  </div>

                  <div>
                    <Label className="text-gray-400 mb-2 block flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Phone Number
                    </Label>
                    {editing ? (
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="bg-black/50 border-white/10 text-white"
                        placeholder="0801 234 5678"
                        data-testid="profile-phone"
                      />
                    ) : (
                      <p className="text-white text-lg">{formData.phone || '-'}</p>
                    )}
                  </div>

                  <div>
                    <Label className="text-gray-400 mb-2 block flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Delivery Address
                    </Label>
                    {editing ? (
                      <Input
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        className="bg-black/50 border-white/10 text-white"
                        placeholder="Your delivery address"
                        data-testid="profile-address"
                      />
                    ) : (
                      <p className="text-white text-lg">{formData.address || '-'}</p>
                    )}
                  </div>
                </div>

                {editing && (
                  <div className="mt-6 flex gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setEditing(false)}
                      className="border-white/10 text-white hover:bg-white/10"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
